/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { cssMap, jsx } from '@compiled/react';

import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { SourcesRightRailCard } from '@atlassian/lumina/sources-right-rail-card';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { useKnowledgeSmartCardAnalytics } from '../knowledge-smart-card/hooks/use-knowledge-smart-card-analytics';

const styles = cssMap({
	container: {
		// Keep in sync with sourcesRailTitle styles in smart-answer-card-consolidated
		width: '314px',
		minWidth: '314px',
		marginLeft: token('space.300'),
	},
	skeletonCard: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.150'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		minHeight: '100px',
	},
});

export const SourcesRightRail = ({ maxHeight }: { maxHeight?: number }): React.JSX.Element => {
	const context = useAIAnswerContext();
	const { commonAttributes, loading } = context;
	const sources = commonAttributes?.sources ?? [];
	const { handleSmartCardClick, searchSessionId } = useKnowledgeSmartCardAnalytics({
		aiAnswerDialogContext: context,
	});

	// maxHeight comes from a ResizeObserver so it can't live in cssMap, it ensures source container height matches answer content.
	// overflow/scrollbar are bundled with it to avoid a scrollbar flash during expand transitions.
	const maxHeightStyle: React.CSSProperties | undefined = maxHeight
		? { maxHeight: `${maxHeight}px`, overflowY: 'auto', scrollbarWidth: 'thin' }
		: undefined;

	if (sources.length === 0) {
		if (!loading) {
			return <Box xcss={styles.container} />;
		}
		return (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic maxHeight based on answer content height
			<Box xcss={styles.container} style={maxHeightStyle}>
				<Stack space="space.150">
					{Array.from({ length: 3 }, (_, i) => (
						<Box key={i} xcss={styles.skeletonCard} />
					))}
				</Stack>
			</Box>
		);
	}

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic maxHeight based on answer content height
		<Box xcss={styles.container} style={maxHeightStyle}>
			<Stack space="space.150">
				{sources.map((source, index) => (
					<SourcesRightRailCard
						key={source.url || index}
						source={source}
						index={index}
						onClickHandler={handleSmartCardClick}
						searchSessionId={searchSessionId}
					/>
				))}
			</Stack>
		</Box>
	);
};
